import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTED_REPORT_MODES } from '../../../reports/types/ReportTypes'
import { FieldErrors, useFormContext } from 'react-hook-form'
import { StandardButtonSecondary } from '../../../ui_reusable/styles/Button.styles'
import { NoteText } from './styles/DriversForm.styles'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import { Message } from 'rsuite'
import { updateModel } from '../../../../api_operations/saveReportOrAndModel'
import ModelLoadingModal from '../../comp_reusable/LoadingModelModal'
import {
  ButtonsWrapper,
  ButtonsWrapperWithNote,
  ErrorSectionLabel,
  ErrorSectionList,
  ErrorSummary,
  RunButtonWithIconWrapper,
  RunModelWrapper,
} from './styles/RunModel.styles'
import {
  ArtifactPolymorphic,
  ProjectSolution,
} from '../../../../api/generated/models'
import { artifactsPredictImpactCreate } from '../../../../api/generated/endpoints/impact/impact'
import {
  artifactsPredictOptimizeCreate,
  artifactsOptimizeTaskRetrieve,
  artifactsOptimizeTaskDestroy,
} from '../../../../api/generated/endpoints/optimize/optimize'
import {
  ImpactArtifactRequest,
  OptimizeArtifactRequest,
} from '../../../../api/generated/models'
import { useMutation } from '@tanstack/react-query'
import { useProjectContext } from '../../../../context/ProjectContext'
import {
  selectedProjectAllowsModelRun,
  useClientProjectContext,
} from '../../../../context/ClientProjectContext'

const normalizeOptimizeRevenuePerKpi = (modelData: any) => {
  if (!modelData?.optimize_config) return

  // Copy so we do not mutate react-hook-form's live values (clears the ROI selects).
  modelData.optimize_config = { ...modelData.optimize_config }

  const revenuePerKpiId = modelData.optimize_config.revenue_per_kpi_id

  if (revenuePerKpiId == null || revenuePerKpiId === '') {
    modelData.optimize_config.revenue_per_kpi_id = null
  }

  const isRevenue = modelData.optimize_config.dependent_driver_is_revenue
  const hasRevenuePerKpi =
    modelData.optimize_config.revenue_per_kpi_id != null

  if (isRevenue === true || hasRevenuePerKpi) {
    modelData.optimize_config.expected_media_contribution = null
    if (!modelData.optimize_config.roi_impression) {
      modelData.optimize_config.roi_impression = 'standard'
    }
    if (!modelData.optimize_config.roi_reach) {
      modelData.optimize_config.roi_reach = 'standard'
    }
  }

  // ROI fields are unused when outcome is KPI and there is no revenue-per-KPI series.
  if (isRevenue === false && !hasRevenuePerKpi) {
    modelData.optimize_config.roi_impression = null
    modelData.optimize_config.roi_reach = null
  }
}

const RunModel = ({
  projectSolution,
}: {
  projectSolution: ProjectSolution
}) => {
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()
  const location = useLocation()

  const { artifact, mode }: { artifact?: ArtifactPolymorphic; mode?: string } =
    location.state
  const { selectedProject } = useProjectContext()
  const { clientProjectData } = useClientProjectContext()
  const canRunModels = selectedProjectAllowsModelRun(
    selectedProject,
    clientProjectData,
  )

  const isEditMode = mode === ROUTED_REPORT_MODES.EDIT_MODEL
  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    handleSubmit,
    getValues,
    formState: { errors, isSubmitted },
  } = useFormContext<OptimizeArtifactRequest | ImpactArtifactRequest>()

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isRunning, setIsRunning] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [taskId, setTaskId] = useState<string | null>(null)
  const [backendErrors, setBackendErrors] = useState<Record<string, string[]>>(
    {},
  )

  // Custom mutations that support AbortSignal
  const createImpactModel = useMutation({
    mutationFn: (data: ImpactArtifactRequest) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      return artifactsPredictImpactCreate(
        data,
        abortControllerRef.current.signal,
      )
    },
  })

  const createOptimizeModel = useMutation({
    mutationFn: (data: OptimizeArtifactRequest) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      return artifactsPredictOptimizeCreate(
        data,
        abortControllerRef.current.signal,
      )
    },
  })

  // Polling function for Optimize task status
  const pollOptimizeTaskStatus = async (
    taskId: string,
    signal?: AbortSignal,
  ): Promise<any> => {
    const maxAttempts = 360 // 360 attempts * 10 seconds = 1 hour max
    let attempts = 0

    while (attempts < maxAttempts) {
      // Check if operation was aborted
      if (signal?.aborted) {
        throw new Error('Polling was cancelled')
      }

      try {
        const response = await artifactsOptimizeTaskRetrieve(taskId, signal)
        const responseData = response as any

        // Check if task is complete (status code 200 means success)
        if (responseData.status === 200 || responseData.data?.artifact) {
          return responseData.data
        }

        // Check if task failed with backend error message
        if (responseData.data?.status.toLowerCase() === 'fail') {
          const error: any = new Error('Optimize task failed')
          error.backendMessage =
            responseData.data?.message || 'Optimize task failed'
          error.isBackendError = true
          throw error
        }

        // Check if task failed
        if (
          responseData.data?.status.toLowerCase() === 'failure' ||
          responseData.data?.status === 'error'
        ) {
          throw new Error(responseData.data?.message || 'Optimize task failed')
        }

        // Task is still pending (202), wait 10 seconds before next poll
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, 10000)
          if (signal) {
            signal.addEventListener('abort', () => {
              clearTimeout(timeout)
              reject(new Error('Polling was cancelled'))
            })
          }
        })
        attempts++
      } catch (error: any) {
        // If operation was aborted, re-throw
        if (
          error.name === 'AbortError' ||
          error.message === 'Polling was cancelled'
        ) {
          throw error
        }

        // If it's a 202 response, continue polling
        if (error.response?.status === 202) {
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(resolve, 10000)
            if (signal) {
              signal.addEventListener('abort', () => {
                clearTimeout(timeout)
                reject(new Error('Polling was cancelled'))
              })
            }
          })
          attempts++
          continue
        }

        // Check if the error response contains a 'fail' status with a message
        if (
          error.response?.data?.status === 'fail' &&
          error.response?.data?.message
        ) {
          const backendError: any = new Error('Optimize task failed')
          backendError.backendMessage = error.response.data.message
          backendError.isBackendError = true
          throw backendError
        }

        // For other errors, throw
        throw error
      }
    }

    throw new Error('Optimize task timed out after 1 hour')
  }

  const handleSaveModel = async () => {
    const modelData = getValues()
    if (!modelData) return

    setIsSaving(true)
    setBackendErrors({}) // Clear previous backend errors
    try {
      if (isEditMode && artifact?.id) {
        // For OPTIMIZE models, map independent_driver_ids to control_driver_ids
        if (projectSolution.solution?.name === 'OPTIMIZE') {
          normalizeOptimizeRevenuePerKpi(modelData)
          ;(modelData as any).control_driver_ids = (
            modelData as any
          ).independent_driver_ids

          // Map selected_region_ids to optimize_config.regions for regional models
          const selectedRegionIds = (modelData as any).selected_region_ids
          if (
            (modelData as any).optimize_config?.geo_granularity ===
              'regional' &&
            Array.isArray(selectedRegionIds)
          ) {
            const numericRegionIds = selectedRegionIds.filter(
              (id: any) => typeof id === 'number',
            )
            ;(modelData as any).optimize_config.regions = numericRegionIds
          } else {
            // TODO: Add here to also send regions in national mode if aggregation is implemented
            ;(modelData as any).optimize_config.regions = null
          }

          // Remove frontend-only fields
          delete (modelData as any).selected_region_ids
          delete (modelData as any).is_national_selected
        }

        const result = await updateModel(
          artifact.id,
          modelData,
          artifact.project_solution?.solution?.name.toLowerCase() ?? '',
        )
        if (result.success) {
          navigate(location.state?.origin || '/models')
        } else {
          setBackendErrors({
            general: [result.error || 'Failed to save model'],
          })
        }
      } else {
        navigate('/models')
      }
    } catch (error: any) {
      console.error('Error saving model:', error)
      setBackendErrors({ general: [error.message || 'Failed to save model'] })
    } finally {
      setIsSaving(false)
    }
  }

  const handleRunModel = async () => {
    if (!canRunModels) {
      return
    }
    setIsRunning(true)
    setBackendErrors({}) // Clear previous backend errors
    let currentProgress = 0
    let interval: NodeJS.Timeout | null = null

    try {
      const values = getValues()

      // Ensure model name is set before running - if not, generate a fallback
      if (!values.name) {
        const generatedName = `model_${new Date().getTime()}`
        values.name = generatedName
      }

      // Add mode and artifact IDs based on current mode
      // NEW_MODEL: mode = 'new' - creates a new artifact
      // EDIT_MODEL: mode = 'explore' + old_artifact_id - creates temporary variant for comparison
      if (isEditMode && artifact?.id) {
        ;(values as any).mode = 'explore'
        ;(values as any).old_artifact_id = artifact.id
      } else {
        // Explicitly set 'new' mode for clarity (backend defaults to this anyway)
        ;(values as any).mode = 'new'
      }

      let res
      if (projectSolution.solution?.name === 'IMPACT') {
        // Impact takes ~30 seconds, update progress every 500ms to reach 95% at 30s
        interval = setInterval(() => {
          currentProgress += 95 / 60 // 95% over 60 intervals (30 seconds)
          if (currentProgress < 95) {
            setProgress(Math.round(currentProgress))
          }
        }, 500)

        delete (values as any).channel_driver_inputs
        delete (values as any).control_driver_ids
        delete (values as any).optimize_config
        delete (values as any).optimize_selected_region_ids

        res = await createImpactModel.mutateAsync(
          values as ImpactArtifactRequest,
        )

        if (interval) clearInterval(interval)
        if (!res) return

        const data = res.data

        // In explore mode, backend returns old_artifact_id and new_artifact_id
        const oldArtifactId = isEditMode
          ? (data as any).old_artifact_id
          : undefined
        const newArtifactId = isEditMode
          ? (data as any).new_artifact_id
          : undefined

        // Create model data for saving later
        // In EDIT_MODEL mode, preserve the original artifact ID
        const modelDataForSave = {
          ...values,
          ...(isEditMode && artifact?.id ? { id: artifact.id } : {}),
        }

        setProgress(100)

        // Get solution name for routing
        const solutionName = projectSolution.solution?.name?.toLowerCase()

        navigate(`/${solutionName}/report`, {
          state: {
            data: data,
            origin: location.state?.origin
              ? location.state.origin
              : '/dashboard',
            mode: location.state?.mode || ROUTED_REPORT_MODES.NEW_MODEL,
            modelData: modelDataForSave,
            // Pass oldModelData in EDIT_MODEL mode to track changes
            ...(isEditMode && artifact ? { oldModelData: artifact } : {}),
            // Pass artifact IDs for replace operation in EDIT_MODEL
            ...(isEditMode && oldArtifactId && newArtifactId
              ? {
                  oldArtifactId,
                  newArtifactId,
                }
              : {}),
          },
        })
      }

      if (projectSolution.solution?.name === 'OPTIMIZE') {
        const payload = {
          ...values,
          optimize_config: (values as any).optimize_config
            ? { ...(values as any).optimize_config }
            : (values as any).optimize_config,
        }
        normalizeOptimizeRevenuePerKpi(payload)

        // Optimize takes ~15 minutes, update progress every 2s to reach 95% at 15min
        interval = setInterval(() => {
          currentProgress += 95 / 450 // 95% over 450 intervals (15 minutes)
          if (currentProgress < 95) {
            setProgress(Math.round(currentProgress))
          }
        }, 2000)

        // @ts-ignore
        payload.control_driver_ids = payload.independent_driver_ids

        // Map selected_region_ids to optimize_config.regions for regional models
        const selectedRegionIds = (payload as any).selected_region_ids
        if (
          (payload as any).optimize_config?.geo_granularity === 'regional' &&
          Array.isArray(selectedRegionIds)
        ) {
          // Filter out 'national' string and keep only numeric region IDs
          const numericRegionIds = selectedRegionIds.filter(
            (id: any) => typeof id === 'number',
          )
          ;(payload as any).optimize_config.regions = numericRegionIds
        } else {
          // For national, explicitly set regions to null
          // TODO: Add here to also send regions in national mode if aggregation is implemented
          ;(payload as any).optimize_config.regions = null
        }

        // Remove frontend-only fields that shouldn't be sent to backend
        delete (payload as any).selected_region_ids
        delete (payload as any).is_national_selected

        // Remove fields Optimize API.
        delete (payload as any).parameters
        delete (payload as any).independent_driver_ids

        res = await createOptimizeModel.mutateAsync(
          payload as OptimizeArtifactRequest,
        )

        if (!res) return

        const taskData = res.data
        const currentTaskId = taskData?.task_id

        if (!currentTaskId) {
          throw new Error('No task_id returned from Optimize model creation')
        }

        // Store task ID for cleanup
        setTaskId(currentTaskId)

        // Continue polling for task completion
        setIsPolling(true)

        const reportData = await pollOptimizeTaskStatus(
          currentTaskId,
          abortControllerRef.current?.signal,
        )

        if (interval) clearInterval(interval)
        setIsPolling(false)
        setTaskId(null) // Clear task ID after successful completion

        // In explore mode, backend returns old_artifact_id and new_artifact_id in the completed task
        const oldArtifactId = isEditMode
          ? (reportData as any).old_artifact_id
          : undefined
        const newArtifactId = isEditMode
          ? (reportData as any).new_artifact_id
          : undefined

        // Create model data for saving later
        // In EDIT_MODEL mode, preserve the original artifact ID
        const modelDataForSave = {
          ...values,
          ...(isEditMode && artifact?.id ? { id: artifact.id } : {}),
        }

        setProgress(100)

        // Get solution name for routing
        const solutionName = projectSolution.solution?.name?.toLowerCase()

        navigate(`/${solutionName}/report`, {
          state: {
            data: reportData,
            origin: location.state?.origin
              ? location.state.origin
              : '/dashboard',
            mode: location.state?.mode || ROUTED_REPORT_MODES.NEW_MODEL,
            modelData: modelDataForSave,
            // Pass oldModelData in EDIT_MODEL mode to track changes
            ...(isEditMode && artifact ? { oldModelData: artifact } : {}),
            // Pass artifact IDs for replace operation in EDIT_MODEL
            ...(isEditMode && oldArtifactId && newArtifactId
              ? {
                  oldArtifactId,
                  newArtifactId,
                }
              : {}),
          },
        })
      }
    } catch (error: any) {
      if (interval) clearInterval(interval)
      setIsPolling(false)

      // Clean up optimize task if it exists
      if (taskId && projectSolution.solution?.name === 'OPTIMIZE') {
        try {
          await artifactsOptimizeTaskDestroy(taskId)
        } catch (cleanupError) {
          console.error('Error cleaning up optimize task:', cleanupError)
        }
        setTaskId(null)
      }

      if (
        error.name !== 'AbortError' &&
        error.message !== 'Polling was cancelled'
      ) {
        console.error('Error running model:', error)

        // Handle Optimize task backend errors
        if (error.isBackendError && error.backendMessage) {
          setBackendErrors({ general: [error.backendMessage] })
          pushToast(
            <Message type="error">
              Optimize task failed: {error.backendMessage}
            </Message>,
            {
              placement: 'topCenter',
              duration: 5000,
            },
          )
        }
        // Parse backend validation errors
        else if (
          error.response?.data?.status === 'fail' &&
          error.response?.data?.message
        ) {
          const backendErrorData = error.response.data.message
          const flattenedErrors: Record<string, string[]> = {}

          // Handle array of error messages (e.g., ["Date matching validation failed..."])
          if (Array.isArray(backendErrorData)) {
            flattenedErrors.general = backendErrorData
          }
          // Handle nested object error structure (e.g., { parameters: { field: ["error"] } })
          else if (typeof backendErrorData === 'object') {
            Object.entries(backendErrorData).forEach(
              ([section, fields]: [string, any]) => {
                if (Array.isArray(fields)) {
                  // Direct array of errors for a field (e.g., { "timeframe": ["This field is required."] })
                  flattenedErrors[section] = fields
                } else if (typeof fields === 'object' && fields !== null) {
                  // Nested object structure (e.g., { "optimize_config": { "budget_type": ["error"] } })
                  Object.entries(fields).forEach(
                    ([field, messages]: [string, any]) => {
                      const key = `${section}.${field}`
                      flattenedErrors[key] = Array.isArray(messages)
                        ? messages
                        : [messages]
                    },
                  )
                }
              },
            )
          }
          // Handle string error message
          else if (typeof backendErrorData === 'string') {
            flattenedErrors.general = [backendErrorData]
          }

          setBackendErrors(flattenedErrors)
        } else if (
          error.response?.data?.status === 'error' &&
          error.response?.data?.message
        ) {
          // Handle string error message
          const errorMessage = error.response.data.message
          setBackendErrors({ general: [errorMessage] })
        } else {
          // Show generic error toast for non-validation errors
          pushToast(
            <Message type="error">
              Error running model: {error.message}
            </Message>,
            {
              placement: 'topCenter',
              duration: 5000,
            },
          )
        }
      }
    } finally {
      setIsRunning(false)
      setProgress(0)
    }
  }

  const categorizeErrors = (
    errors: FieldErrors<ImpactArtifactRequest | OptimizeArtifactRequest>,
    backendErrors: Record<string, string[]>,
  ) => {
    const sections: {
      [key: string]: {
        label: string
        errors: string[]
      }
    } = {
      parameters: { label: 'Set Parameters', errors: [] },
      channels: { label: 'Select channels', errors: [] },
      drivers: { label: 'Select drivers', errors: [] },
    }

    const generalErrors: string[] = []

    const collectFrontendMessages = (
      node: unknown,
      path: string,
    ): Array<{ path: string; message: string }> => {
      if (!node || typeof node !== 'object') return []

      const record = node as Record<string, unknown>
      if (typeof record.message === 'string' && record.message) {
        return [{ path, message: record.message }]
      }

      const collected: Array<{ path: string; message: string }> = []
      Object.entries(record).forEach(([key, value]) => {
        if (key === 'ref' || key === 'type' || key === 'types' || key === 'root') {
          return
        }
        const nextPath = path ? `${path}.${key}` : key
        collected.push(...collectFrontendMessages(value, nextPath))
      })
      return collected
    }

    const bucketForPath = (path: string) => {
      if (
        path.includes('independent_driver') ||
        path.includes('control_driver')
      ) {
        return 'drivers'
      }
      if (path.includes('channel_driver')) {
        return 'channels'
      }
      return 'parameters'
    }

    collectFrontendMessages(errors, '').forEach(({ path, message }) => {
      sections[bucketForPath(path)].errors.push(message)
    })

    // Handle backend validation errors
    Object.entries(backendErrors).forEach(([key, messages]) => {
      // Ensure messages is an array
      const messageArray = Array.isArray(messages) ? messages : [messages]

      messageArray.forEach((message) => {
        // Format the field name for display
        const fieldName = key.split('.').pop()?.replace(/_/g, ' ') || key
        const formattedMessage =
          key === 'general' ? message : `${fieldName}: ${message}`

        // Do not include 'general' backend errors in parameter/driver sections.
        // Keep them separate so they aren't shown as a field-level validation item.
        if (key === 'general') {
          generalErrors.push(formattedMessage)
        } else if (
          key.includes('independent_driver') ||
          key.includes('control_driver')
        ) {
          sections.drivers.errors.push(formattedMessage)
        } else if (key.includes('channel_driver')) {
          sections.channels.errors.push(formattedMessage)
        } else {
          sections.parameters.errors.push(formattedMessage)
        }
      })
    })

    return {
      sections: Object.values(sections).filter(
        (section) => section.errors.length > 0,
      ),
      generalErrors,
    }
  }

  const handleClose = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Clean up optimize task if it exists
    if (taskId && projectSolution.solution?.name === 'OPTIMIZE') {
      try {
        await artifactsOptimizeTaskDestroy(taskId)
      } catch (error) {
        console.error('Error cleaning up optimize task on cancel:', error)
      }
      setTaskId(null)
    }

    setIsRunning(false)
    setProgress(0)
    setIsPolling(false)
  }

  // Only surface client-side field errors after the user tries to run/save.
  // Nested optimize_config validators are registered on mount with empty defaults
  // and must not paint the summary on first paint.
  const categorized = categorizeErrors(
    isSubmitted ? errors : {},
    backendErrors,
  )
  const errorSummaryRef = useRef<HTMLDivElement | null>(null)
  const hasVisibleErrors =
    categorized.generalErrors.length > 0 || categorized.sections.length > 0

  useEffect(() => {
    if (!hasVisibleErrors) return
    errorSummaryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [hasVisibleErrors, categorized.sections.length, categorized.generalErrors.length])

  return (
    <RunModelWrapper>
      <div ref={errorSummaryRef}>
        {hasVisibleErrors && (
          <ErrorSummary>
            {/* Render any general backend errors separately (not inside a labeled section) */}
            {categorized.generalErrors.length > 0 && (
              <ErrorSectionList>
                {categorized.generalErrors.map((error, idx) => (
                  <li key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                    {error}
                  </li>
                ))}
              </ErrorSectionList>
            )}

            {categorized.sections.length !== 0 && (
              <h4>In order to run a model, the following must be set:</h4>
            )}
            {categorized.sections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <ErrorSectionLabel isFirst={sectionIndex === 0}>
                  {section.label}:
                </ErrorSectionLabel>
                <ErrorSectionList>
                  {section.errors.map((error, errorIndex) => (
                    <li key={errorIndex} style={{ whiteSpace: 'pre-wrap' }}>
                      {error}
                    </li>
                  ))}
                </ErrorSectionList>
              </div>
            ))}
          </ErrorSummary>
        )}
      </div>
      <ButtonsWrapperWithNote>
        <ButtonsWrapper>
          {isEditMode && (
            <StandardButtonSecondary
              onClick={handleSaveModel}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save & Exit'}
            </StandardButtonSecondary>
          )}
          <RunButtonWithIconWrapper
            onClick={handleSubmit(handleRunModel)}
            disabled={!canRunModels}
            title={
              canRunModels
                ? undefined
                : 'Reactivate the project before running a model.'
            }
          >
            RUN
          </RunButtonWithIconWrapper>
        </ButtonsWrapper>
        {isEditMode && (
          <NoteText>
            Note! Running will not save model, but you can save it later
          </NoteText>
        )}
      </ButtonsWrapperWithNote>
      <ModelLoadingModal
        open={isRunning}
        progress={progress}
        handleClose={handleClose}
        isPolling={isPolling}
        solutionName={projectSolution.solution?.name}
      />
    </RunModelWrapper>
  )
}

export default RunModel
